<template>
  <div v-if="sections.length" class="host-mini-map">
    <svg class="host-mini-map__svg" :viewBox="viewBoxString" role="img" aria-label="Selector de zonas del mapa dinámico">
      <g v-for="section in sections" :key="section.id" class="host-mini-map__zone" :class="zoneClass(section)" @click="toggleSection(section)">
        <rect
          :x="sectionShape(section).x"
          :y="sectionShape(section).y"
          :width="sectionShape(section).w"
          :height="sectionShape(section).h"
          :rx="sectionShape(section).rx || 26"
        />
        <text :x="sectionShape(section).x + sectionShape(section).w / 2" :y="sectionShape(section).y + 46" text-anchor="middle">{{ section.name }}</text>
      </g>
      <g v-if="seats.length" class="host-mini-map__seats">
        <g
          v-for="seat in seats"
          :key="seat.key"
          class="host-mini-map__seat"
          :class="seatClass(seat)"
          :transform="seatTransform(seat)"
          @click.stop="selectSeat(seat)"
        >
          <rect :width="seat.w || 34" :height="seat.h || 26" rx="7" />
          <text v-if="seatValue === seat.key" :x="(seat.w || 34) / 2" :y="(seat.h || 26) / 2 + 4" text-anchor="middle">{{ seat.label }}</text>
        </g>
      </g>
    </svg>
    <p class="host-mini-map__hint">{{ helperText }}</p>
  </div>
  <div v-else class="host-mini-map host-mini-map--empty">
    <span>No hay layout de mapa disponible para este evento.</span>
  </div>
</template>

<script>
export default {
  name: 'HostDynamicVenueMiniMap',
  props: {
    sections: { type: Array, default: () => [] },
    modelValue: { type: Array, default: () => [] },
    mode: { type: String, default: 'zone' },
    viewBox: { type: Object, default: null },
    seats: { type: Array, default: () => [] },
    seatValue: { type: String, default: '' },
    interactiveSeats: { type: Boolean, default: false },
    readonlySections: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'update:seatValue'],
  computed: {
    viewBoxString() {
      const box = this.viewBox || { x: 0, y: -70, width: 1620, height: 1290 }
      return `${box.x} ${box.y} ${box.width} ${box.height}`
    },
    helperText() {
      if (this.interactiveSeats) return 'Elige un asiento disponible dentro de la zona seleccionada.'
      return this.mode === 'seat'
        ? 'Elige zonas con asientos numerados. Las zonas sin seats quedan bloqueadas.'
        : 'Elige la zona visual donde se venderá esta etapa.'
    },
  },
  methods: {
    canSelect(section) {
      if (this.mode !== 'seat') return true
      return section.has_seats !== false && section.kind !== 'zone'
    },
    sectionShape(section) {
      const shape = (section && (section.shape || section.view_box)) || {}
      return {
        x: Number.parseFloat(shape.x) || 0,
        y: Number.parseFloat(shape.y) || 0,
        w: Number.parseFloat(shape.w || shape.width) || 0,
        h: Number.parseFloat(shape.h || shape.height) || 0,
        rx: Number.parseFloat(shape.rx) || 26,
      }
    },
    isSelected(section) {
      return this.modelValue.includes(section.id)
    },
    zoneClass(section) {
      return {
        'is-selected': this.isSelected(section),
        'is-disabled': !this.canSelect(section),
        'is-readonly': this.readonlySections,
      }
    },
    toggleSection(section) {
      if (this.readonlySections) return
      if (!this.canSelect(section)) return
      const next = this.modelValue.slice()
      const index = next.indexOf(section.id)
      if (index >= 0) next.splice(index, 1)
      else next.push(section.id)
      this.$emit('update:modelValue', next)
    },
    seatTransform(seat) {
      const rotation = Number.parseFloat(seat.rotation) || 0
      const width = Number.parseFloat(seat.w) || 34
      const height = Number.parseFloat(seat.h) || 26
      return `translate(${seat.x || 0} ${seat.y || 0}) rotate(${rotation} ${width / 2} ${height / 2})`
    },
    canSelectSeat(seat) {
      return this.interactiveSeats && seat && seat.state === 'available'
    },
    seatClass(seat) {
      return {
        'is-selected': this.seatValue === seat.key,
        'is-disabled': !this.canSelectSeat(seat),
      }
    },
    selectSeat(seat) {
      if (!this.canSelectSeat(seat)) return
      this.$emit('update:seatValue', seat.key)
    },
  },
}
</script>

<style scoped>
.host-mini-map {
  display: grid;
  gap: 10px;
  padding: 14px;
  border-radius: 20px;
  border: 1px solid rgba(210, 220, 245, 0.12);
  background: radial-gradient(circle at 20% 0%, rgba(177, 157, 228, 0.12), transparent 36%), rgba(255, 255, 255, 0.025);
}

.host-mini-map__svg {
  display: block;
  width: 100%;
  min-height: 260px;
  border-radius: 16px;
  background: linear-gradient(145deg, rgba(8, 11, 17, 0.96), rgba(4, 6, 10, 0.98));
}

.host-mini-map__zone {
  cursor: pointer;
}

.host-mini-map__zone rect {
  fill: rgba(126, 145, 184, 0.18);
  stroke: rgba(220, 229, 251, 0.38);
  stroke-width: 3;
  transition: fill 160ms ease, stroke 160ms ease, opacity 160ms ease;
}

.host-mini-map__zone text {
  fill: #eef3ff;
  font-size: 32px;
  font-weight: 900;
  pointer-events: none;
}

.host-mini-map__zone.is-selected rect {
  fill: rgba(112, 84, 184, 0.42);
  stroke: #f8f5ff;
}

.host-mini-map__zone.is-disabled {
  cursor: not-allowed;
  opacity: 0.24;
}

.host-mini-map__zone.is-readonly {
  cursor: default;
}

.host-mini-map__seats {
  pointer-events: auto;
}

.host-mini-map__seat {
  cursor: pointer;
}

.host-mini-map__seat rect {
  fill: rgba(235, 240, 255, 0.72);
  stroke: rgba(18, 21, 32, 0.5);
  stroke-width: 1.5;
  transition: fill 160ms ease, stroke 160ms ease, opacity 160ms ease;
}

.host-mini-map__seat text {
  fill: #120f1d;
  font-size: 12px;
  font-weight: 900;
  pointer-events: none;
}

.host-mini-map__seat.is-selected rect {
  fill: #f8f5ff;
  stroke: #7a5bd6;
  stroke-width: 4;
}

.host-mini-map__seat.is-disabled {
  cursor: not-allowed;
  opacity: 0.24;
}

.host-mini-map__hint,
.host-mini-map--empty span {
  margin: 0;
  color: rgba(214, 225, 236, 0.74);
  font-size: 0.82rem;
  line-height: 1.45;
}
</style>
